/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { gte } from '@ember/object/computed';
import { isEmpty, isNone } from '@ember/utils';
import { scheduleOnce } from '@ember/runloop';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import AuthService from 'cio/services/auth';
import EventBusService, {
  EMAIL_COMPOSER_ROLLBACK,
  LIQUID_ENABLED_FIELD_FOCUS_IN,
  LIQUID_ENABLED_FIELD_FOCUS_OUT,
} from 'cio/services/event-bus';
import EnvironmentService from 'cio/services/environment';
import IdentityModel from 'cio/models/identity';
import { addNewHeader, removeHeader, updateHeaderName, updateHeaderValue } from 'cio/utils/models/header';
import {
  ComposerCampaignActionTemplate,
  ComposerNewsletterTemplate,
  ComposerTransactionMessageTemplate,
} from 'cio/utils/composer/composer-editable-entity';
import { EmailRenderErrors } from 'cio/utils/previews';
import { IdentityOption } from 'cio/utils/action-composer';

interface ActionComposerEnvelopeArgs {
  editableEntity: ComposerCampaignActionTemplate | ComposerNewsletterTemplate | ComposerTransactionMessageTemplate;
  errors?: EmailRenderErrors;
  onBlur?: () => unknown;
  hasReadOnlyPermission: boolean;
}

export default class ActionComposerEnvelope extends Component<ActionComposerEnvelopeArgs> {
  @service environment!: EnvironmentService;
  @service auth!: AuthService;
  @service eventBus!: EventBusService;

  readonly defaultRecipient = '{{customer.email}}';
  readonly templateType = 'email'; // only supporting email right now

  @tracked isHeadersExpanded = true;
  @tracked isReplyToVisible = false;
  @tracked isBccVisible = false;
  @tracked isEnvelopeExpanded = false;
  @tracked isPreheaderTextDisplayed = false;
  @tracked envelopeElement: HTMLElement | null = null;

  constructor(owner: unknown, args: ActionComposerEnvelopeArgs) {
    super(owner, args);
    this.isPreheaderTextDisplayed = !isEmpty(this.template.preheaderText);
    this.subscribeToRollback();
  }

  @action didInsert(element: HTMLElement) {
    this.envelopeElement = element;
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromRollback();
    window.removeEventListener('mousedown', this.onExternalInteraction);
    window.removeEventListener('blur', this.onExternalInteraction);
  }

  @action
  onEnvelopeToggle() {
    this.isEnvelopeExpanded = !this.isEnvelopeExpanded;

    if (this.isEnvelopeExpanded) {
      window.addEventListener('mousedown', this.onExternalInteraction);
      window.addEventListener('blur', this.onExternalInteraction); // catch editor iframe clicks
    } else {
      window.removeEventListener('mousedown', this.onExternalInteraction);
      window.removeEventListener('blur', this.onExternalInteraction);
    }
  }

  @action
  onExternalInteraction(event: MouseEvent) {
    if (
      this.envelopeElement == null ||
      !this.isEnvelopeExpanded ||
      (event.type === 'mousedown' && this.envelopeElement.contains(event.target as Node))
    ) {
      return;
    }

    this.onEnvelopeToggle();
  }

  @action
  liquidFieldFocusIn(fieldName: string, index = 0) {
    this.eventBus.publish(LIQUID_ENABLED_FIELD_FOCUS_IN, [fieldName, index]);
  }

  @action
  liquidFieldFocusOut() {
    this.eventBus.publish(LIQUID_ENABLED_FIELD_FOCUS_OUT);
  }

  get template() {
    return this.args.editableEntity.template;
  }

  @computed('args.errors.headers.[]')
  get headerErrors() {
    return this.args.errors?.headers ?? [];
  }

  @computed('args.errors.subject.[]')
  get subjectErrors() {
    return this.args.errors?.subject ?? [];
  }

  @computed('template.preheaderText', 'isPreheaderTextDisplayed')
  get isPreheaderTextVisible() {
    if (this.isPreheaderTextDisplayed) {
      return true;
    }

    return !isEmpty(this.template.preheaderText);
  }

  @gte('template.headers.length', 10) hasMaximumHeaders!: boolean;

  @computed('template.headers.[]', 'isReplyToVisible', 'isBccVisible')
  get headerCount() {
    let count = this.template.headers.length || 0;

    if (this.isReplyToVisible) {
      count++;
    }

    if (this.isBccVisible) {
      count++;
    }

    return count;
  }

  @computed('environment.current.identities.@each.templateType')
  get identities(): IdentityModel[] {
    return this.environment.current.identities.filter((identity) => identity.templateType === this.templateType);
  }

  // used to conditionally show "From" address dropdown or CTA to setup sender and verify a new identity
  @computed('identities.@each.verified')
  get hasSelectableIdentity() {
    return this.identities.some((identity: IdentityModel) => identity.verified !== 'unverified');
  }

  // options formatted for use in fly-power-select dropdown
  @computed('identities.@each.{address,verified}')
  get identitiesOptions(): IdentityOption[] {
    return _sortBy(
      this.identities.map((identity: IdentityModel) => {
        let domain = this.environment.domainForIdentity(identity);
        let unverified = identity.verified === 'unverified';

        return {
          domain,
          id: identity.id,
          address: identity.address,
          verified: identity.verified,
          emailDomain: identity.emailDomain,
          unverified,
          unavailableForNonTransactional: false,
          displayIpPool: false,
          disabled: unverified,
        } as IdentityOption;
      }),
      ['disabled'],
    ); // show disabled sender identities as last in the list
  }

  // options without "disabled" property: and so selectable in the address dropdown
  // this is used when choosing "Reply-to" identity, which doesn't have to be verified
  @computed('identitiesOptions')
  get identitiesOptionsAllEnabled() {
    return this.identitiesOptions.map((i) => ({ ...i, disabled: false }));
  }

  @action
  toggleReplyToAndBcc() {
    if (!isNone(this.template.replyToIdentity)) {
      this.isReplyToVisible = true;
    }

    if (!isNone(this.template.bcc)) {
      this.isBccVisible = true;
    }
  }

  @action
  subscribeToRollback() {
    this.eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
  }

  @action
  unsubscribeFromRollback() {
    this.eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
  }

  _onEmailComposerRollback() {
    if (isNone(this.template.replyToIdentity)) {
      this.isReplyToVisible = false;
    }

    if (isNone(this.template.bcc)) {
      this.isBccVisible = false;
    }
  }

  @action
  onFromIdentitySelect(identityOption: IdentityOption) {
    this.template.set(
      'fromIdentity',
      this.identities.find((identity) => identity.id === identityOption.id),
    );
  }

  @action
  onReplyToIdentitySelect(identityOption: IdentityOption) {
    this.template.set(
      'replyToIdentity',
      this.identities.find((identity) => identity.id === identityOption.id),
    );
  }

  @action
  changeRecipient() {
    this.template.set('recipient', '');

    scheduleOnce('afterRender', this, this.setRecipientFocus);
  }

  setRecipientFocus() {
    (document.querySelector('.js-editor-field-to .fly-form-control') as HTMLInputElement)?.focus();
  }

  @action
  useDefaultRecipient() {
    this.template.set('recipient', null);
  }

  @action
  showPreheaderText() {
    this.isPreheaderTextDisplayed = true;

    scheduleOnce('afterRender', this, this.setPreheaderFocus);
  }

  setPreheaderFocus() {
    (document.querySelector('.js-preheader-text-input') as HTMLInputElement)?.focus();
  }

  @action
  removePreheaderText() {
    this.isPreheaderTextDisplayed = false;
    this.template.preheaderText = null;
  }

  @action
  clearReplyTo() {
    this.isReplyToVisible = false;
    this.template.set('replyToIdentity', null);
  }

  @action
  showReplyTo() {
    this.template.set('replyToIdentity', this.template.fromIdentity);

    this.isReplyToVisible = true;
    this.isHeadersExpanded = true;

    scheduleOnce('afterRender', this, this.setReplyToFocus);
  }

  setReplyToFocus() {
    (document.querySelector('.js-editor-field-reply-to .fly-form-control') as HTMLInputElement)?.focus();
  }

  @action
  showBcc() {
    this.isBccVisible = true;
    this.isHeadersExpanded = true;

    scheduleOnce('afterRender', this, this.setBccFocus);
  }

  setBccFocus() {
    (document.querySelector('.js-editor-field-bcc .fly-form-control') as HTMLInputElement)?.focus();
  }

  @action
  removeBcc() {
    this.isBccVisible = false;
    this.template.set('bcc', null);
  }

  @action
  addHeader() {
    this.template.headers = addNewHeader(this.template.headers);
    this.isHeadersExpanded = true;
  }

  @action
  removeHeader(index: number) {
    this.template.headers = removeHeader(this.template.headers, index);
  }

  @action
  updateHeaderName(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    this.template.headers = updateHeaderName(this.template.headers, index, event.target.value);
  }

  @action
  updateHeaderValue(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    this.template.headers = updateHeaderValue(this.template.headers, index, event.target.value);
  }

  @action
  toggleHeadersExpanded() {
    this.isHeadersExpanded = !this.isHeadersExpanded;
  }

  @action
  setTemplateFakeBcc() {
    if (!this.template.hasBcc) {
      this.template.set('fakeBcc', true);
    }
  }
}
